import React, { useState } from 'react';
import apiService from '../../services/api';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';


interface MaccabiTransferModalProps {
    isOpen: boolean;
    onClose: () => void;
    task?: any;
    token: string;
    onSuccess?: () => void;
    onStatusMessage?: (message: { type: 'success' | 'error' | 'loading'; text: string } | null) => void;
}

export const MaccabiTransferModal: React.FC<MaccabiTransferModalProps> = ({
    isOpen,
    onClose,
    task,
    token,
    onSuccess,
    onStatusMessage,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [moveClinicStatus, setMoveClinicStatus] = useState<string>('none');
    const [comments, setComments] = useState<string>('');

    // Reset state when modal opens and populate with task data
    React.useEffect(() => {
        if (isOpen && task) {
            setMoveClinicStatus('none');
            setComments(task.move_clinic_comments || '');
        }
    }, [isOpen, task]);

    // Handle save
    const handleSave = async () => {
        if (!task || !token) return;

        setIsSubmitting(true);
        onStatusMessage?.({ type: 'loading', text: 'Saving Maccabi transfer...' });

        try {
            const response = await apiService.movePatient({
                token,
                patient_id: task.id,
                move_clinic_status: moveClinicStatus,
                move_clinic_comments: comments,
            });

            // Check if task should be removed
            if (response.op === 'delete') {
                onStatusMessage?.({ type: 'success', text: 'Task removed successfully' });
            } else {
                onStatusMessage?.({ type: 'success', text: 'Maccabi transfer saved successfully' });
            }

            onSuccess?.();
            handleClose();
        } catch (error: any) {
            console.error('Failed to save Maccabi transfer:', error);
            onStatusMessage?.(({
                type: 'error',
                text: error.message || 'Failed to save Maccabi transfer'
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle close
    const handleClose = () => {
        onClose();
    };

    if (!isOpen) return null;

    return (
        <ModalWrapper modalId="MaccabiTransferModal"
            isOpen={isOpen}
            onClose={handleClose}
            title="Maccabi Transfer"
            isLoading={isSubmitting}
        >
            <div className="space-y-4">
                {/* Status Dropdown */}
                <div className="flex items-center gap-4">
                    <select
                        value={moveClinicStatus}
                        onChange={(e) => setMoveClinicStatus(e.target.value)}
                        className={`${POPUP_STYLES.input} flex-1`}
                    >
                        <option value="none">None</option>
                        <option value="Interested">Interested</option>
                        <option value="Not Interested">Not Interested</option>
                    </select>
                </div>

                {/* Comment Field */}
                <div className="flex items-start gap-4">
                    <label className="font-semibold text-[#366A75] min-w-[100px] pt-2">Comment</label>
                    <textarea
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        className={`${POPUP_STYLES.input} flex-1 resize-none`}
                        placeholder="Enter value"
                        rows={3}
                    />
                </div>

                {/* Buttons */}
                <div className="flex items-center justify-center gap-4 pt-4">
                    <button
                        onClick={handleSave}
                        disabled={isSubmitting}
                        className={POPUP_STYLES.button}
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                    <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className={POPUP_STYLES.button}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </ModalWrapper>
    );
};
